import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IoClose, IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { login } from "../redux/authSlice";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import GoogleSocialLogin from "../components/Goooglelogin";
import {
  clearStoredReferralId,
  resolveReferralId,
} from "../utils/referral";
import { trackEvent } from "../analytics";
import {
  addCartItem,
  flushPendingCartItems,
  queuePendingCartItem,
} from "../utils/pendingCart";
import PasswordRequirements from "../components/PasswordRequirements";
import { getPasswordError } from "../utils/passwordValidation";
import "./CheckoutAuthModal.css";

const emptyRegister = {
  password: "",
  email: "",
  role: "P",
  full_name: "",
  terms_and_conditions: false,
  important_info: false,
  promotional_emails: false,
};

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const CheckoutAuthModal = ({
  show,
  onHide,
  onAuthenticated,
  product,
  intent = "buy",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchDataprofile, fetchcart } = useIndexPageData();
  const referralId = resolveReferralId(location.search);
  const [mode, setMode] = useState("register");
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [registerForm, setRegisterForm] = useState(emptyRegister);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const isRegister = mode === "register";
  const isBuy = intent === "buy";
  const cartItem = product?.productId
    ? {
        product_id: product.productId,
        modelType: product.modelType || "listing",
      }
    : null;

  const addCurrentItemToCart = async () => {
    if (cartItem) {
      queuePendingCartItem(cartItem);
      await addCartItem(cartItem);
    }
    await flushPendingCartItems();
    await fetchcart();
  };

  useEffect(() => {
    if (!show) return;
    setMode("register");
    setLoader(false);
    setShowPassword(false);
    setLoginForm({ username: "", password: "" });
    setRegisterForm(emptyRegister);
    setPasswordTouched(false);
  }, [show]);

  const finishAuthenticated = async (role, userId) => {
    if (userId) {
      await fetchDataprofile(userId);
    }
    if (role === "A") {
      onHide();
      navigate("/admin/dashboard");
      return;
    }
    if (role === "F") {
      onHide();
      navigate("/affiliate/dashboard");
      return;
    }
    onAuthenticated?.();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoader(true);
      const response = await apiClient.post("/login/", loginForm);
      if (!response.ok) return handleErrors(response.data);
      toast.success("login successfully");
      const role = response?.data?.role;
      dispatch(
        login({
          token: response.data?.token,
          userType: role,
          role,
          userid: response?.data?.user_id,
        })
      );
      if (role !== "A" && role !== "F") {
        await addCurrentItemToCart();
      }
      await finishAuthenticated(role, response?.data?.user_id);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (getPasswordError(registerForm.password)) {
      setPasswordTouched(true);
      return;
    }
    try {
      setLoader(true);
      const payload = {
        ...registerForm,
        redirectTo: "checkout",
        ...(referralId ? { referral_id: referralId } : {}),
      };
      const response = await apiClient.post("/register/", payload);
      if (!response.ok) return handleErrors(response.data);
      trackEvent("sign_up", {
        method: "email",
        registration_context: "checkout",
      });
      if (cartItem) queuePendingCartItem(cartItem);
      clearStoredReferralId();
      toast.success(
        "Check your email to activate your account. This item will be waiting in your cart."
      );
      onHide();
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const headline = isRegister
    ? isBuy
      ? "Create an account to finish checkout"
      : "Create an account to add this to your cart"
    : isBuy
      ? "Log in to finish checkout"
      : "Log in to add this to your cart";

  const switchLink = isRegister ? (
    <>
      Already registered?{" "}
      <button
        type="button"
        className="btn btn-link p-0 align-baseline fw-semibold"
        style={{ color: "#c93312", textDecoration: "underline" }}
        onClick={() => setMode("login")}
      >
        Log in
      </button>
    </>
  ) : (
    <>
      First time here?{" "}
      <button
        type="button"
        className="btn btn-link p-0 align-baseline fw-semibold"
        style={{ color: "#c93312", textDecoration: "underline" }}
        onClick={() => setMode("register")}
      >
        Create an account
      </button>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      enforceFocus={false}
      dialogClassName="checkout-auth-dialog"
    >
      <Modal.Body>
        <div className="checkout-auth">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <img
              src="../assets/images/mockee-logo.png"
              alt="Mockee"
              className="checkout-auth__logo"
            />
            <button
              type="button"
              className="btn btn-light rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
              style={{ width: 32, height: 32 }}
              onClick={onHide}
              aria-label="Close"
            >
              <IoClose size={18} />
            </button>
          </div>

          {product ? (
            <div className="d-flex align-items-center gap-2 bg-white rounded-3 px-2 py-2 mb-3">
              {product.image ? (
                <img
                  src={product.image}
                  alt=""
                  className="checkout-auth__thumb"
                />
              ) : (
                <div className="checkout-auth__thumb d-flex align-items-center justify-content-center text-muted">
                  M
                </div>
              )}
              <div className="checkout-auth__copy d-flex align-items-center justify-content-between gap-2 flex-grow-1">
                <span className="fw-semibold text-truncate" style={{ fontSize: 13 }}>
                  {product.title || "Mockup"}
                </span>
                {product.price ? (
                  <strong className="small mb-0 flex-shrink-0">{product.price}</strong>
                ) : null}
              </div>
            </div>
          ) : null}

          <h5 className="fw-bold text-dark mb-1" style={{ lineHeight: 1.3, fontSize: 20 }}>
            {headline}
          </h5>
          {isRegister ? (
            <p className="small mb-3" style={{ color: "#4f564b" }}>
              It&apos;s not another subscription — your account will always be free.
            </p>
          ) : (
            <div className="mb-3" />
          )}

          {isRegister ? (
            <form onSubmit={handleRegister}>
              <input
                required
                type="email"
                className="form-control checkout-auth__input mb-2"
                placeholder="Email"
                value={registerForm.email}
                onChange={(e) =>
                  setRegisterForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
              <input
                required
                type="text"
                className="form-control checkout-auth__input mb-2"
                placeholder="Name"
                value={registerForm.full_name}
                onChange={(e) =>
                  setRegisterForm((prev) => ({
                    ...prev,
                    full_name: e.target.value,
                  }))
                }
              />
              <div className="position-relative mb-3">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  className="form-control checkout-auth__input pe-5"
                  placeholder="Password"
                  autoComplete="new-password"
                  value={registerForm.password}
                  onChange={(e) => {
                    setPasswordTouched(true);
                    setRegisterForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }));
                  }}
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                </button>
                <PasswordRequirements
                  password={registerForm.password}
                  touched={passwordTouched}
                />
              </div>

              <div className="form-check checkout-auth__check d-flex align-items-start gap-2 mb-1 ps-0">
                <input
                  type="checkbox"
                  className="form-check-input ms-0"
                  id="checkout-promo"
                  checked={registerForm.promotional_emails}
                  onChange={(e) =>
                    setRegisterForm((prev) => ({
                      ...prev,
                      promotional_emails: e.target.checked,
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="checkout-promo"
                  style={{ fontSize: 11, color: "#5b6257", lineHeight: 1.45 }}
                >
                  I agree to receive commercial information about news and
                  offers from Mockee. I have also read the{" "}
                  <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </Link>{" "}
                  and the{" "}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>

              <div className="form-check checkout-auth__check d-flex align-items-start gap-2 mb-2 ps-0">
                <input
                  required
                  type="checkbox"
                  className="form-check-input ms-0"
                  id="checkout-terms"
                  checked={registerForm.terms_and_conditions}
                  onChange={(e) =>
                    setRegisterForm((prev) => ({
                      ...prev,
                      terms_and_conditions: e.target.checked,
                    }))
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="checkout-terms"
                  style={{ fontSize: 11, color: "#5b6257", lineHeight: 1.45 }}
                >
                  I declare that I have read the{" "}
                  <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </Link>{" "}
                  and the Privacy Policy and accept their provisions.
                </label>
              </div>

              <button
                type="submit"
                className="btn grey-btn w-100 rounded-3 py-2"
                disabled={loader}
              >
                {loader ? "Creating account..." : "Create account and continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin}>
              <input
                required
                type="email"
                className="form-control checkout-auth__input mb-2"
                placeholder="Email"
                value={loginForm.username}
                onChange={(e) =>
                  setLoginForm((prev) => ({ ...prev, username: e.target.value }))
                }
              />
              <div className="position-relative mb-2">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  className="form-control checkout-auth__input pe-5"
                  placeholder="Password"
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                </button>
              </div>
              <div className="text-end mb-3">
                <Link
                  to="/forgot-password"
                  className="small"
                  style={{ color: "#3f463c" }}
                  onClick={onHide}
                >
                  Forgot password?
                </Link>
              </div>
              <button
                type="submit"
                className="btn grey-btn w-100 rounded-3 py-2"
                disabled={loader}
              >
                {loader ? "Signing in..." : "Log in and continue"}
              </button>
            </form>
          )}

          <div className="checkout-auth__or">or</div>

          <GoogleSocialLogin
            variant="button"
            stayOnPage
            analyticsContext="checkout"
            onHide={onHide}
            onSuccess={async () => {
              await addCurrentItemToCart();
              onAuthenticated?.();
            }}
            label={
              <span className="d-inline-flex align-items-center gap-2">
                <GoogleMark />
                Sign in with Google
              </span>
            }
          />

          <p className="small text-center mb-0 mt-3" style={{ color: "#4f564b" }}>
            {switchLink}
          </p>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CheckoutAuthModal;
